import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../contexts/LanguageContext';

const SEO = ({ 
  title, 
  description, 
  image, 
  url, 
  type = 'website',
  article = null,
  noindex = false 
}) => {
  const { language } = useLanguage();
  
  const siteName = 'PASTORIA SERRALTA';
  const siteUrl = 'https://www.pastoria-serralta.com';
  const defaultImage = `${siteUrl}/og-image.jpg`;
  const twitterHandle = '@pastoriaserralta';
  
  const defaultDescriptions = {
    es: 'PASTORIA SERRALTA - Promotor inmobiliario de referencia en Baleares. Viviendas de calidad para la clase media residente en Palma de Mallorca.',
    en: 'PASTORIA SERRALTA - Leading real estate developer in the Balearic Islands. Quality homes for local middle-class residents in Palma de Mallorca.'
  };

  const defaultTitles = {
    es: 'PASTORIA SERRALTA | Promotor Inmobiliario en Baleares',
    en: 'PASTORIA SERRALTA | Real Estate Developer in Balearic Islands'
  };

  const finalTitle = title || defaultTitles[language];
  const finalDescription = description || defaultDescriptions[language];
  const finalImage = image || defaultImage;
  const finalUrl = url ? `${siteUrl}${url}` : siteUrl;
  const alternateUrl = language === 'es' 
    ? finalUrl.replace('/en/', '/').replace('/en', '/') 
    : finalUrl.includes('/en') ? finalUrl : finalUrl.replace(siteUrl, `${siteUrl}/en`);

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="title" content={finalTitle} />
      <meta name="description" content={finalDescription} />
      <meta name="author" content="PASTORIA SERRALTA, S.L." />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'} />
      <meta name="language" content={language === 'es' ? 'Spanish' : 'English'} />
      <meta name="revisit-after" content="7 days" />
      
      {/* Geographic Meta Tags */}
      <meta name="geo.region" content="ES-IB" />
      <meta name="geo.placename" content="Palma de Mallorca" />
      <meta name="geo.position" content="39.5696;2.6502" />
      <meta name="ICBM" content="39.5696, 2.6502" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={finalUrl} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={finalTitle} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={language === 'es' ? 'es_ES' : 'en_GB'} />
      <meta property="og:locale:alternate" content={language === 'es' ? 'en_GB' : 'es_ES'} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={finalUrl} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:creator" content={twitterHandle} />

      {/* Article specific (for blog posts if needed) */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          <meta property="article:modified_time" content={article.modifiedTime} />
          <meta property="article:author" content={article.author || 'PASTORIA SERRALTA'} />
          <meta property="article:section" content={article.section} />
        </>
      )}

      {/* Canonical and Alternate URLs */}
      <link rel="canonical" href={finalUrl} />
      <link rel="alternate" href={finalUrl} hrefLang={language} />
      <link rel="alternate" href={alternateUrl} hrefLang={language === 'es' ? 'en' : 'es'} />
      <link rel="alternate" href={siteUrl} hrefLang="x-default" />
    </Helmet>
  );
};

export default SEO;
